import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AlertCircle, ArrowLeft, ExternalLink, Eye, Loader2, Search, UsersRound,
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const authConfig = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

const normalizePhone = (value) => {
  const digits = String(value || '').replace(/\D/g, '');
  return digits.length >= 10 ? digits.slice(-10) : digits;
};
const normalizeEmail = (value) => String(value || '').trim().toLowerCase();
const normalizeName = (value) => String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');

const mergeGroups = (target, source, groups, phoneMap, emailMap, nameMap) => {
  if (!source || source === target) return target;
  source.leads.forEach(lead => target.leads.push(lead));
  groups.splice(groups.indexOf(source), 1);
  phoneMap.forEach((group, key) => { if (group === source) phoneMap.set(key, target); });
  emailMap.forEach((group, key) => { if (group === source) emailMap.set(key, target); });
  nameMap.forEach((group, key) => { if (group === source) nameMap.set(key, target); });
  return target;
};

export const groupLeadsByCustomer = (leads = []) => {
  const groups = [];
  const phoneMap = new Map();
  const emailMap = new Map();
  const nameMap = new Map();

  leads.forEach(lead => {
    const phone = normalizePhone(lead.phone);
    const email = normalizeEmail(lead.email);
    const name = normalizeName(lead.name);
    const matches = [
      phone ? phoneMap.get(phone) : null,
      email ? emailMap.get(email) : null,
      !phone && !email && name ? nameMap.get(name) : null,
    ].filter(Boolean);
    let group = matches[0];
    if (!group) {
      group = { leads: [] };
      groups.push(group);
    }
    matches.slice(1).forEach(match => { group = mergeGroups(group, match, groups, phoneMap, emailMap, nameMap); });
    group.leads.push(lead);
    if (phone) phoneMap.set(phone, group);
    if (email) emailMap.set(email, group);
    if (!phone && !email && name) nameMap.set(name, group);
  });

  return groups.map(group => {
    group.leads.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
    const latest = group.leads[0];
    return {
      ...group,
      id: latest.id,
      name: latest.name || group.leads.find(lead => lead.name)?.name || 'Unnamed Customer',
      phone: latest.phone || group.leads.find(lead => lead.phone)?.phone || '',
      email: latest.email || group.leads.find(lead => lead.email)?.email || '',
      latest,
    };
  }).sort((a, b) => new Date(b.latest?.created_at || 0) - new Date(a.latest?.created_at || 0));
};

const formatDate = (value) => value
  ? new Date(value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
  : '-';

const StatusBadge = ({ status }) => {
  const styles = {
    Open: 'bg-blue-100 text-blue-700',
    'In Progress': 'bg-amber-100 text-amber-700',
    Complete: 'bg-violet-100 text-violet-700',
    'Closed Won': 'bg-green-100 text-green-700',
    'Closed Lost': 'bg-red-100 text-red-700',
  };
  return <span className={`inline-flex rounded px-2 py-1 text-xs font-semibold ${styles[status] || 'bg-slate-100 text-slate-700'}`}>{status || '-'}</span>;
};

const useLeads = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  useEffect(() => {
    const fetchLeads = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/leads`, authConfig());
        setLeads(response.data || []);
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to load leads data');
      } finally {
        setLoading(false);
      }
    };
    fetchLeads();
  }, []);
  return { leads, loading, error };
};

export const LeadsData = () => {
  const navigate = useNavigate();
  const { leads, loading, error } = useLeads();
  const [search, setSearch] = useState('');
  const customers = useMemo(() => groupLeadsByCustomer(leads), [leads]);
  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return customers;
    return customers.filter(customer => [customer.name, customer.phone, customer.email]
      .some(value => String(value || '').toLowerCase().includes(query)));
  }, [customers, search]);

  return (
    <div data-testid="leads-data-page">
      <div className="mb-8">
        <h1 className="text-3xl font-bold uppercase tracking-tight text-slate-900">Leads Data</h1>
        <p className="mt-1 text-slate-600">Unique customers and their complete lead history</p>
      </div>

      {error && <div className="mb-6 flex items-center gap-2 border border-red-200 bg-red-50 px-4 py-3 text-red-700"><AlertCircle className="h-5 w-5" />{error}</div>}

      <div className="mb-6 rounded-sm border border-slate-200 bg-white p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={event => setSearch(event.target.value)}
            placeholder="Search by customer name, phone or email..." data-testid="leads-data-search"
            className="w-full rounded-sm border border-slate-200 py-2 pl-10 pr-4 focus:border-orange-500 focus:outline-none" />
        </div>
      </div>

      <div className="rounded-sm border border-slate-200 bg-white">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center">
            <UsersRound className="mx-auto mb-4 h-16 w-16 text-slate-300" />
            <h3 className="text-xl font-semibold text-slate-700">No customers found</h3>
            <p className="mt-2 text-slate-500">{leads.length ? 'Try a different search.' : 'Customer history will appear when leads are created.'}</p>
          </div>
        ) : (
          <>
            <div className="hidden grid-cols-12 gap-4 border-b border-slate-200 bg-slate-50 px-6 py-4 md:grid">
              <div className="col-span-3 text-sm font-semibold uppercase tracking-wider text-slate-700">Customer</div>
              <div className="col-span-3 text-sm font-semibold uppercase tracking-wider text-slate-700">Contact</div>
              <div className="col-span-2 text-sm font-semibold uppercase tracking-wider text-slate-700">Total Leads</div>
              <div className="col-span-2 text-sm font-semibold uppercase tracking-wider text-slate-700">Latest Lead</div>
              <div className="text-sm font-semibold uppercase tracking-wider text-slate-700">Status</div>
              <div className="text-sm font-semibold uppercase tracking-wider text-slate-700">Actions</div>
            </div>
            {filtered.map(customer => (
              <div key={customer.id} className="grid grid-cols-1 items-center gap-4 border-b border-slate-100 px-6 py-4 hover:bg-slate-50 md:grid-cols-12"
                data-testid={`lead-customer-${customer.id}`}>
                <div className="md:col-span-3"><p className="font-semibold text-slate-900">{customer.name}</p></div>
                <div className="text-sm text-slate-600 md:col-span-3">
                  <p>{customer.phone || '-'}</p><p className="mt-1 text-xs">{customer.email || '-'}</p>
                </div>
                <div className="md:col-span-2"><span className="inline-flex min-w-8 justify-center rounded-full bg-orange-100 px-2.5 py-1 text-xs font-bold text-orange-700">{customer.leads.length}</span></div>
                <div className="text-sm text-slate-600 md:col-span-2">{formatDate(customer.latest.created_at)}</div>
                <div><StatusBadge status={customer.latest.status} /></div>
                <div>
                  <button onClick={() => navigate(`/admin/services/leads-data/${customer.id}`)}
                    className="rounded p-2 text-slate-600 hover:bg-orange-50 hover:text-orange-600" title="View complete lead history"
                    data-testid={`view-lead-customer-${customer.id}`}><Eye className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
};

export const LeadsDataDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { leads, loading, error } = useLeads();
  const customer = useMemo(
    () => groupLeadsByCustomer(leads).find(group => group.leads.some(lead => lead.id === id)),
    [leads, id],
  );

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>;

  return (
    <div data-testid="leads-data-detail-page">
      <button onClick={() => navigate('/admin/services/leads-data')} className="mb-6 inline-flex items-center gap-2 text-slate-600 hover:text-slate-900">
        <ArrowLeft className="h-4 w-4" /> Back to Leads Data
      </button>
      {error || !customer ? (
        <div className="flex items-center gap-2 border border-red-200 bg-red-50 px-4 py-3 text-red-700"><AlertCircle className="h-5 w-5" />{error || 'Customer lead history not found'}</div>
      ) : (
        <>
          <div className="mb-6 rounded-sm border border-slate-200 bg-white p-6">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Customer Lead History</p>
            <h1 className="mt-1 text-3xl font-bold uppercase tracking-tight text-slate-900">{customer.name}</h1>
            <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-3">
              <div><p className="text-xs font-semibold uppercase text-slate-500">Phone</p><p className="mt-1 text-sm text-slate-900">{customer.phone || '-'}</p></div>
              <div><p className="text-xs font-semibold uppercase text-slate-500">Email</p><p className="mt-1 text-sm text-slate-900">{customer.email || '-'}</p></div>
              <div><p className="text-xs font-semibold uppercase text-slate-500">Total Leads</p><p className="mt-1 text-sm font-bold text-orange-600">{customer.leads.length}</p></div>
            </div>
          </div>

          <div className="rounded-sm border border-slate-200 bg-white">
            <div className="border-b border-slate-200 bg-slate-50 px-6 py-4"><h2 className="text-lg font-bold uppercase tracking-tight text-slate-900">All Leads</h2></div>
            {customer.leads.map(lead => (
              <div key={lead.id} className="grid grid-cols-1 items-center gap-4 border-b border-slate-100 px-6 py-5 md:grid-cols-12" data-testid={`customer-lead-${lead.id}`}>
                <div className="md:col-span-2"><p className="text-xs font-semibold uppercase text-slate-500">Created</p><p className="mt-1 text-sm text-slate-800">{formatDate(lead.created_at)}</p></div>
                <div className="md:col-span-2"><p className="text-xs font-semibold uppercase text-slate-500">Lead Source</p><p className="mt-1 text-sm text-slate-800">{lead.lead_source_name || '-'}</p></div>
                <div className="md:col-span-3"><p className="text-xs font-semibold uppercase text-slate-500">Product</p><p className="mt-1 text-sm text-slate-800">{lead.product || '-'}</p></div>
                <div className="md:col-span-2"><StatusBadge status={lead.status} /></div>
                <div className="md:col-span-2"><p className="text-xs font-semibold uppercase text-slate-500">Requirements</p><p className="mt-1 line-clamp-2 text-sm text-slate-600">{lead.requirements || '-'}</p></div>
                <div>
                  <button onClick={() => navigate(`/admin/services/leads/${lead.id}`)}
                    className="inline-flex items-center gap-1 rounded-sm bg-orange-500 px-3 py-2 text-xs font-bold uppercase text-white hover:bg-orange-600"
                    data-testid={`open-lead-${lead.id}`}>Open <ExternalLink className="h-3 w-3" /></button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};
